import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import CommonSubHeader from 'components/commonSubHeader/CommonSubHeader';
import CommonTable from 'components/commonTable/CommonTable';
import Loader from 'components/loader/Loader';
import AuditTrailDetails from 'components/AuditTrail/AuditTrailDetails';
import CommonModal from 'components/commonModal/commonModal';

import {
  getAuditTrailList,
  getAuditTrailMeta,
  getAuditTrailDetails,
} from 'redux/actions/AuditTrailAction';

const AuditTrail = () => {
  const dispatch = useDispatch();

  // ---------------- STATES ----------------
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);

  const today = new Date().toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  const [showSelectMessage, setShowSelectMessage] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  // ---------------- REDUX SELECTORS ----------------
  const auditMeta = useSelector((state) => state?.getAuditTrailMeta?.auditTrailMeta?.data);

  const auditTable = useSelector((state) => state?.getAuditTrailList?.auditTrailList?.data);

  const auditDetails = useSelector((state) => state?.getAuditTrailDetails?.auditTrailDetails?.data);

  const tableLoader = useSelector((state) => state?.getAuditTrailList?.loading);

  const metaLoader = useSelector((state) => state?.getAuditTrailMeta?.loading);

  const detailLoader = useSelector((state) => state?.getAuditTrailDetails?.loading);

  // ---------------- META API ----------------
  useEffect(() => {
    dispatch(getAuditTrailMeta());
  }, []);

  // ---------------- LIST API ----------------
  useEffect(() => {
    if (!showSelectMessage) {
      dispatch(
        getAuditTrailList({
          entityType: selectedEntity?.value || '',
          companyId: selectedCompany?.value || '',
          action: selectedAction?.value || '',
          dateFrom,
          dateTo,
          page: page + 1,
          pageSize: rowsPerPage,
        }),
      );
    }
  }, [
    page,
    rowsPerPage,
    selectedEntity,
    selectedCompany,
    selectedAction,
    dateFrom,
    dateTo,
    showSelectMessage,
  ]);

  // ---------------- OPTIONS ----------------
  const entityOptions = auditMeta?.entityTypes || [];

  const actionOptions = auditMeta?.actions || [];

  const companyOptions =
    auditMeta?.companies?.map((i) => ({
      label: i.name,
      value: i.id,
    })) || [];

  // ---------------- APPLY FILTER ----------------
  const applyFilters = () => {
    setShowSelectMessage(false);
    setPage(0);
  };

  // ---------------- HANDLERS ----------------
  const onEntityChange = (e, value) => {
    setSelectedEntity(value);
    applyFilters();
  };

  const onCompanyChange = (e, value) => {
    setSelectedCompany(value);
    applyFilters();
  };

  const onActionChange = (e, value) => {
    setSelectedAction(value);
    applyFilters();
  };

  const onSelectDateRange = (value) => {
    setDateFrom(value?.dateFrom || '');
    setDateTo(value?.dateTo || '');
    applyFilters();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  const tableOnClickRow = (navigate, row) => {
    dispatch(getAuditTrailDetails(row.id));
    setDetailOpen(true);
  };

  // ---------------- TABLE FORMAT ----------------
  const staticHeaders = [
    { label: 'Entity', key: 'entityLabel' },
    { label: 'Action', key: 'actionLabel' },
    { label: 'Performed By', key: 'performedByName' },
    // { label: "Role", key: "performedByRole" },
    { label: 'Company', key: 'companyName' },
    { label: 'Date Time', key: 'createdAt' },
  ];

  const auditTrailTableData = {
    header: staticHeaders,
    // One company on this box has a name beginning with a literal tab, which
    // the table renders as written and so indents the whole Company column.
    // Trimmed here rather than in CommonTable, which every other page shares.
    body: (auditTable?.body || []).map((row) => ({
      ...row,
      companyName: String(row?.companyName || '').trim(),
    })),
    totalCount: auditTable?.totalCount || 0,
  };

  // ---------------- RENDER ----------------
  if (metaLoader) {
    return <Loader height={'60vh'} />;
  }

  return (
    <Box className="mb20">
      {/* FILTERS */}
      <Box className="mb30">
        <CommonSubHeader
          showEntityDropdown={true}
          entityOptions={entityOptions}
          selectedEntity={selectedEntity}
          onEntityChange={onEntityChange}
          showCompanyDropdown={true}
          companyOptions={companyOptions}
          selectedCompany={selectedCompany}
          onCompanyChange={onCompanyChange}
          showActionDropdown={true}
          actionOptions={actionOptions}
          selectedAction={selectedAction}
          onActionChange={onActionChange}
          showDateRange={true}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onSelectDateRange={onSelectDateRange}
        />
      </Box>

      {/* MESSAGE / LOADER / TABLE */}
      {showSelectMessage ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '40vh',
          }}
        >
          <Typography className="colorGrey">
            Please select Entity, Action and Date Range to view audit logs.
          </Typography>
        </Box>
      ) : tableLoader ? (
        <Loader height={'60vh'} />
      ) : (
        <CommonTable
          data={auditTrailTableData}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={auditTrailTableData.totalCount}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          tableOnClickRow={tableOnClickRow}
          isClickable={true}
        />
      )}

      {/* DETAIL MODAL */}
      <CommonModal open={detailOpen} handleClose={() => setDetailOpen(false)}>
        <AuditTrailDetails data={auditDetails} loading={detailLoader} />
      </CommonModal>
    </Box>
  );
};

export default AuditTrail;
